import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, Download, Menu, MonitorCog, Moon, Sun, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { navigation, profile } from '../../data/portfolio';
import { useScrollProgress, useTheme, type ThemePreference } from '../../hooks/usePortfolio';

export function DownloadCVButton({ compact = false }: { compact?: boolean }) {
  return <a className={compact ? 'button button-small button-primary' : 'button button-primary'} href="/Ajith_Redrigo_CV.pdf" download><Download size={17} aria-hidden="true" /> Download CV</a>;
}

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const options: { value: ThemePreference; label: string; icon: typeof Moon }[] = [
    { value: 'dark', label: 'Dark', icon: Moon }, { value: 'light', label: 'Light', icon: Sun }, { value: 'system', label: 'System', icon: MonitorCog },
  ];
  return <div className="theme-toggle" role="group" aria-label="Colour theme">
    {options.map(({ value, label, icon: Icon }) => <button key={value} type="button" className={theme === value ? 'active' : ''} onClick={() => setTheme(value)} aria-label={`${label} theme`} aria-pressed={theme === value}><Icon size={15} /></button>)}
  </div>;
}

export function DesktopNavigation({ activeSection }: { activeSection: string }) {
  return <nav className="desktop-nav" aria-label="Primary navigation">{navigation.map((item) => {
    const section = item.href.slice(1);
    return <a key={item.label} href={item.href} className={activeSection === section ? 'active' : ''} aria-current={activeSection === section ? 'location' : undefined}>{item.label}</a>;
  })}</nav>;
}

export function MobileNavigation({ open, onClose, activeSection }: { open: boolean; onClose: () => void; activeSection: string }) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('menu-open');
    document.addEventListener('keydown', escape);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.classList.remove('menu-open');
      document.removeEventListener('keydown', escape);
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence>{open && <>
    <motion.button className="menu-scrim" aria-label="Close navigation" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
    <motion.aside className="mobile-drawer" role="dialog" aria-modal="true" aria-label="Mobile navigation" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ duration: .2 }}>
      <div className="drawer-head"><span className="brand-mark">AR</span><button ref={closeButtonRef} className="icon-button" onClick={onClose} aria-label="Close menu"><X /></button></div>
      <nav>{navigation.map((item) => {
        const section = item.href.slice(1);
        return <a key={item.label} href={item.href} className={activeSection === section ? 'active' : ''} aria-current={activeSection === section ? 'location' : undefined} onClick={onClose}>{item.label}</a>;
      })}</nav>
      <ThemeToggle /><DownloadCVButton />
    </motion.aside>
  </>}</AnimatePresence>, document.body);
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let frame = 0;
    const sectionIds = navigation.map((item) => item.href.slice(1));
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 14);
        const marker = window.scrollY + Math.min(190, window.innerHeight * .3);
        let current = 'home';
        sectionIds.forEach((id) => {
          const section = document.getElementById(id);
          if (section && section.offsetTop <= marker) current = id;
        });
        setActiveSection(current);
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { window.cancelAnimationFrame(frame); window.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, []);
  return <header className={`navbar ${scrolled ? 'scrolled' : ''}`}><div className="nav-inner">
    <a href="#home" className="brand" aria-label="Ajith Redrigo home"><span className="brand-mark">AR</span><span><strong>Ajith Redrigo</strong><small>Infrastructure · DevOps · Cloud</small></span></a>
    <DesktopNavigation activeSection={activeSection} />
    <div className="nav-actions"><ThemeToggle /><DownloadCVButton compact /><button className="menu-button icon-button" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open}><Menu /></button></div>
  </div><MobileNavigation open={open} onClose={() => setOpen(false)} activeSection={activeSection} /></header>;
}

export function ScrollProgress() {
  const progress = useScrollProgress();
  return <div className="scroll-progress" style={{ width: `${progress}%` }} aria-hidden="true" />;
}

type NetworkNode = {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  radius: number;
  phase: number;
};

export function AmbientBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const pointer = { x: -1000, y: -1000, active: false };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let nodes: NetworkNode[] = [];
    let width = window.innerWidth;
    let height = window.innerHeight;
    let animationFrame = 0;
    let brandColour = '#39bdf8';
    let accentColour = '#36e1d0';

    const readThemeColours = () => {
      const styles = window.getComputedStyle(document.documentElement);
      brandColour = styles.getPropertyValue('--brand').trim() || '#39bdf8';
      accentColour = styles.getPropertyValue('--brand-2').trim() || '#36e1d0';
    };

    const createNodes = () => {
      const areaBasedCount = Math.round((width * height) / 24000);
      const nodeCount = Math.min(76, Math.max(width < 640 ? 28 : 38, areaBasedCount));
      nodes = Array.from({ length: nodeCount }, (): NetworkNode => {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.08 + Math.random() * 0.17;
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          velocityX: Math.cos(angle) * speed,
          velocityY: Math.sin(angle) * speed,
          radius: 1.1 + Math.random() * 1.35,
          phase: Math.random() * Math.PI * 2,
        };
      });
    };

    const drawNetwork = (time = 0, updatePositions = true) => {
      const connectionDistance = width < 640 ? 106 : 138;
      const pointerDistance = width < 640 ? 120 : 165;
      context.clearRect(0, 0, width, height);

      nodes.forEach((node) => {
        if (updatePositions) {
          node.x += node.velocityX;
          node.y += node.velocityY;

          if (node.x < -8) node.x = width + 8;
          if (node.x > width + 8) node.x = -8;
          if (node.y < -8) node.y = height + 8;
          if (node.y > height + 8) node.y = -8;

          if (pointer.active) {
            const deltaX = node.x - pointer.x;
            const deltaY = node.y - pointer.y;
            const distance = Math.hypot(deltaX, deltaY);
            if (distance > 0 && distance < pointerDistance) {
              const force = (1 - distance / pointerDistance) * 0.045;
              node.x += (deltaX / distance) * force;
              node.y += (deltaY / distance) * force;
            }
          }
        }
      });

      context.lineWidth = 0.75;
      for (let index = 0; index < nodes.length; index += 1) {
        const first = nodes[index];
        for (let targetIndex = index + 1; targetIndex < nodes.length; targetIndex += 1) {
          const second = nodes[targetIndex];
          const distance = Math.hypot(first.x - second.x, first.y - second.y);
          if (distance >= connectionDistance) continue;

          context.globalAlpha = (1 - distance / connectionDistance) * 0.32;
          context.strokeStyle = brandColour;
          context.beginPath();
          context.moveTo(first.x, first.y);
          context.lineTo(second.x, second.y);
          context.stroke();
        }
      }

      if (pointer.active && !reducedMotion.matches) {
        nodes.forEach((node) => {
          const distance = Math.hypot(node.x - pointer.x, node.y - pointer.y);
          if (distance >= pointerDistance) return;
          context.globalAlpha = (1 - distance / pointerDistance) * 0.42;
          context.strokeStyle = accentColour;
          context.beginPath();
          context.moveTo(pointer.x, pointer.y);
          context.lineTo(node.x, node.y);
          context.stroke();
        });
      }

      nodes.forEach((node, index) => {
        const pulse = reducedMotion.matches ? 0 : Math.sin(time * 0.0012 + node.phase) * 0.25;
        context.globalAlpha = index % 5 === 0 ? 0.82 : 0.58;
        context.fillStyle = index % 4 === 0 ? accentColour : brandColour;
        context.beginPath();
        context.arc(node.x, node.y, Math.max(0.8, node.radius + pulse), 0, Math.PI * 2);
        context.fill();
      });
      context.globalAlpha = 1;
    };

    const animate = (time: number) => {
      if (document.hidden || reducedMotion.matches) return;
      drawNetwork(time, true);
      animationFrame = window.requestAnimationFrame(animate);
    };

    const restartAnimation = () => {
      window.cancelAnimationFrame(animationFrame);
      drawNetwork(0, false);
      if (!document.hidden && !reducedMotion.matches) {
        animationFrame = window.requestAnimationFrame(animate);
      }
    };

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      createNodes();
      restartAnimation();
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || reducedMotion.matches) return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.active = true;
    };
    const clearPointer = () => {
      pointer.active = false;
    };
    const handleVisibilityChange = () => restartAnimation();
    const handleMotionChange = () => restartAnimation();
    const themeObserver = new MutationObserver(() => {
      readThemeColours();
      if (reducedMotion.matches) drawNetwork(0, false);
    });

    readThemeColours();
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', clearPointer);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotion.addEventListener('change', handleMotionChange);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('pointermove', handlePointerMove);
      document.documentElement.removeEventListener('pointerleave', clearPointer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      reducedMotion.removeEventListener('change', handleMotionChange);
      themeObserver.disconnect();
    };
  }, []);

  return <div className="ambient-background" aria-hidden="true">
    <canvas ref={canvasRef} className="particle-network" />
  </div>;
}

export function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const update = () => setVisible(window.scrollY > 700); update(); window.addEventListener('scroll', update, { passive: true }); return () => window.removeEventListener('scroll', update); }, []);
  return <button className={`back-top ${visible ? 'visible' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top"><ArrowUp /></button>;
}

export function Footer() {
  return <footer className="footer"><div className="container footer-grid"><div><span className="eyebrow">Built for reliability</span><h2>{profile.name}</h2><p>{profile.title}<br />Dubai, United Arab Emirates</p></div><div className="footer-actions"><a href={`mailto:${profile.contact.email}`}>{profile.contact.email}</a><a href={profile.contact.linkedIn} target="_blank" rel="noreferrer">LinkedIn</a><DownloadCVButton /></div></div><div className="container footer-bottom"><span>© {new Date().getFullYear()} Ajith Redrigo</span><span>Infrastructure · DevOps · Aviation Technology</span></div></footer>;
}

export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <AnimatePresence mode="wait"><motion.div key={location.pathname} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: .28 }}>{children}</motion.div></AnimatePresence>;
}

export function LoadingScreen() { return <div className="loading-screen" role="status"><span className="brand-mark">AR</span><span>Loading portfolio</span></div>; }

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (hash) window.setTimeout(() => document.querySelector(hash)?.scrollIntoView(), 0); else window.scrollTo(0, 0); }, [pathname, hash]);
  return <><AmbientBackground /><a className="skip-link" href="#main-content">Skip to content</a><ScrollProgress /><Navbar /><main id="main-content"><PageTransition>{children}</PageTransition></main><Footer /><BackToTop /></>;
}
